import { useEffect, useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, Buildings, Trash, ArrowClockwise, TrendUp, TrendDown, Minus, Flame, CaretDown, CaretUp, X, Warning, CheckCircle, BellRinging, Timer, Snowflake } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import { LeadReplyTools } from "../components/LeadReplyTools";
import { LeadCallLog } from "../components/LeadCallLog";
import { playCashSound } from "../lib/cashSound";
import { FollowupPayoffStrip } from "../components/FollowupPayoffStrip";
import { ReferralThanksCard } from "../components/ReferralThanksCard";
import { NudgeEffectivenessCard } from "../components/NudgeEffectivenessCard";
import { PlaybookNudgeCard } from "../components/PlaybookNudgeCard";
import { SignupSourcesCard } from "../components/SignupSourcesCard";
import { SourceRoiCard } from "../components/SourceRoiCard";
import { ChatConversationsCard } from "../components/ChatConversationsCard";
import { SignupWinPathCard } from "../components/SignupWinPathCard";
import { TwilioStatusBadge } from "../components/TwilioStatusBadge";

const STATUS_OPTIONS = [
  { value: "new", label: "New", pill: "info" },
  { value: "replied", label: "Replied", pill: "success" },
  { value: "contacted", label: "Contacted", pill: "warning" },
  { value: "won", label: "Won", pill: "success" },
  { value: "lost", label: "Lost", pill: "error" },
];

// Fleet-size auto-tag — maps the fleet_size string from lead forms into a segment
// tier (Owner-Op / SMB / Growth / Mid-Market / Enterprise) with a color-coded pill.
// Handles both the dropdown values (e.g. "5-24") and free-text values (e.g. "12 trucks")
// by parsing the first number.
function fleetTier(fleetSize) {
  if (!fleetSize) return null;
  const s = String(fleetSize).trim();
  // Try to parse the first integer in the string
  const match = s.match(/\d+/);
  const n = match ? parseInt(match[0], 10) : NaN;
  if (Number.isNaN(n)) return null;
  if (n <= 4) return { label: "Owner-Op", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" };
  if (n <= 24) return { label: "SMB", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
  if (n <= 99) return { label: "Growth", cls: "bg-[#FF5A1F]/10 text-[#FF9764] border-[#FF5A1F]/30" };
  if (n <= 249) return { label: "Mid-Market", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" };
  return { label: "Enterprise", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" };
}

// Reply Timer — hours a fresh lead has been waiting for a first reply.
// Only flags trusted, still-"new" leads with no manual reply after 2h.
function replyWaitHours(it) {
  if (it.status !== "new" || it.manual_reply_sent_at || it.email_trusted === false) return null;
  const h = (Date.now() - new Date(it.created_at).getTime()) / 36e5;
  return h >= 2 ? h : null;
}

function waitLabel(h) {
  return h >= 48 ? `${Math.floor(h / 24)}d` : `${Math.floor(h)}h`;
}

// Lead Heat Score — fleet size + source + engagement + freshness (0-100).
function heatScore(it) {
  if (it.email_trusted === false || it.status === "won") return 0;
  let s = 0;
  const m = String(it.fleet_size || "").match(/\d+/);
  const n = m ? parseInt(m[0], 10) : 0;
  if (n >= 100) s += 30; else if (n >= 25) s += 25; else if (n >= 5) s += 18; else if (n >= 1) s += 10;
  const utm = (it.utm_source || "").toLowerCase();
  const src = it.source || "";
  if (src === "demo_booking") s += 22;
  else if (utm === "facebook") s += 18;
  else if (utm === "tiktok" || utm === "reels") s += 14;
  else s += 6;
  if (it.signed_up_at) s += 25;
  else if (it.replied_at) s += 20;
  const ageH = (Date.now() - new Date(it.created_at).getTime()) / 36e5;
  if (ageH <= 24) s += 15; else if (ageH <= 72) s += 10; else if (ageH <= 168) s += 5;
  return Math.min(100, s);
}

function heatBadge(score) {
  if (score >= 55) return { label: "HOT", cls: "border-[#FF5A1F]/50 bg-[#FF5A1F]/15 text-[#FF9764]" };
  if (score >= 30) return { label: "WARM", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" };
  return { label: "COLD", cls: "border-white/10 bg-white/[0.03] text-neutral-500" };
}

function FleetTag({ fleetSize, size = "sm" }) {  const tier = fleetTier(fleetSize);
  if (!tier) return null;
  const dims =
    size === "lg"
      ? "text-xs px-2.5 py-1 tracking-wider"
      : "text-[10px] px-2 py-0.5 tracking-wider";
  return (
    <span
      data-testid={`fleet-tag-${tier.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      className={`inline-flex items-center rounded-full border font-mono uppercase font-semibold whitespace-nowrap ${dims} ${tier.cls}`}
    >
      {tier.label}
    </span>
  );
}

// Color-coded chip variants for the filter bar. Match FleetTag palette so mental
// mapping between tag → chip is instant.
const CHIP_VARIANTS = {
  neutral: {
    active: "bg-white text-black border-white",
    idle: "bg-transparent text-neutral-300 border-white/15 hover:border-white/30",
  },
  sky: {
    active: "bg-sky-500/20 text-sky-200 border-sky-500/60",
    idle: "bg-transparent text-sky-300/70 border-sky-500/20 hover:border-sky-500/50",
  },
  emerald: {
    active: "bg-emerald-500/20 text-emerald-200 border-emerald-500/60",
    idle: "bg-transparent text-emerald-300/70 border-emerald-500/20 hover:border-emerald-500/50",
  },
  orange: {
    active: "bg-[#FF5A1F]/20 text-[#FF9764] border-[#FF5A1F]/60",
    idle: "bg-transparent text-[#FF9764]/70 border-[#FF5A1F]/20 hover:border-[#FF5A1F]/50",
  },
  violet: {
    active: "bg-violet-500/20 text-violet-200 border-violet-500/60",
    idle: "bg-transparent text-violet-300/70 border-violet-500/20 hover:border-violet-500/50",
  },
  rose: {
    active: "bg-rose-500/20 text-rose-200 border-rose-500/60",
    idle: "bg-transparent text-rose-300/70 border-rose-500/20 hover:border-rose-500/50",
  },
};

function FilterChip({ label, count, active, onClick, testid, variant = "neutral" }) {
  const v = CHIP_VARIANTS[variant] || CHIP_VARIANTS.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60 ${
        active ? v.active : v.idle
      }`}
    >
      <span>{label}</span>
      <span
        className={`font-mono text-[10px] px-1.5 rounded-full ${active ? "bg-black/20" : "bg-white/5"}`}
      >
        {count}
      </span>
    </button>
  );
}

// Weekly stat card — big number + WoW delta indicator + optional trending flame.
const WEEK_STAT_VARIANTS = {
  neutral: { accent: "text-white", border: "border-white/10", bg: "bg-white/[0.02]" },
  orange: { accent: "text-[#FF9764]", border: "border-[#FF5A1F]/25", bg: "bg-[#FF5A1F]/[0.04]" },
  emerald: { accent: "text-emerald-300", border: "border-emerald-500/25", bg: "bg-emerald-500/[0.04]" },
  sky: { accent: "text-sky-300", border: "border-sky-500/25", bg: "bg-sky-500/[0.04]" },
};

function WeekStatCard({ label, value, delta, variant = "neutral", hot = false, testid, expanded = false, onClick }) {
  const v = WEEK_STAT_VARIANTS[variant] || WEEK_STAT_VARIANTS.neutral;
  const deltaPositive = delta > 0;
  const deltaNegative = delta < 0;
  const deltaColor = deltaPositive
    ? "text-emerald-400"
    : deltaNegative
    ? "text-rose-400"
    : "text-neutral-500";
  const DeltaIcon = deltaPositive ? TrendUp : deltaNegative ? TrendDown : Minus;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-expanded={expanded}
      className={`text-left rounded-xl border ${v.border} ${v.bg} p-4 relative overflow-hidden transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60 ${
        expanded ? "ring-2 ring-[#FF5A1F]/60 -translate-y-0.5" : "hover:-translate-y-0.5 hover:border-white/25"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-neutral-400">{label}</div>
        <div className="flex items-center gap-1.5">
          {hot && (
            <span
              data-testid={`${testid}-hot`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#FF9764] bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 rounded-full px-1.5 py-0.5"
              title="Trending up 30%+ vs last week"
            >
              <Flame size={10} weight="fill" /> Trending
            </span>
          )}
          {expanded ? (
            <CaretUp size={12} className="text-[#FF9764]" weight="bold" />
          ) : (
            <CaretDown size={12} className="text-neutral-500" />
          )}
        </div>
      </div>
      <div className={`font-mono text-3xl font-semibold ${v.accent} leading-none`}>
        {value}
      </div>
      <div className={`text-[11px] mt-2 inline-flex items-center gap-1 ${deltaColor}`}>
        <DeltaIcon size={11} weight="bold" />
        {delta === 0 ? "no change" : `${deltaPositive ? "+" : ""}${delta}% vs last wk`}
      </div>
    </button>
  );
}

// Drill-down panel — shown when a WeekStatCard is expanded. Breaks down the
// segment's this-week volume by source with a horizontal proportional bar,
// plus WoW delta per source. Answers: "Which channel is driving this segment?"
const SEGMENT_LABELS = {
  total: "All leads",
  highArr: "High-ARR leads",
  smb: "SMB leads",
  ownerOp: "Owner-Op leads",
};
const SOURCE_LABELS = {
  free_assessment: "Free Assessment",
  demo_booking: "Demo Booking",
  roi_calculator: "ROI Savings Review",
  contact_page: "Contact Page",
};
const SOURCE_COLORS = {
  free_assessment: "bg-[#FF5A1F]",
  demo_booking: "bg-violet-500",
  roi_calculator: "bg-emerald-500",
  contact_page: "bg-sky-500",
};

function SourceBreakdownPanel({ segmentKey, thisWeekSources, lastWeekSources, onClose }) {
  const rows = ["free_assessment", "demo_booking", "roi_calculator", "contact_page"];
  const total = rows.reduce((sum, k) => sum + (thisWeekSources[k] || 0), 0);
  const maxCount = Math.max(1, ...rows.map((k) => thisWeekSources[k] || 0));
  const wow = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return Math.round(((curr - prev) / prev) * 100);
  };

  return (
    <div
      data-testid={`source-breakdown-${segmentKey}`}
      className="mt-3 rounded-xl border border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.03] p-5 relative"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="source-breakdown-close"
        className="absolute top-3 right-3 text-neutral-500 hover:text-white p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F]/60"
        aria-label="Close breakdown"
      >
        <X size={14} />
      </button>
      <div className="flex items-baseline gap-3 mb-4">
        <div className="overline text-[#FF9764]">Source attribution</div>
        <div className="text-sm text-neutral-300">
          {SEGMENT_LABELS[segmentKey]} · this week
          <span className="text-neutral-500 ml-2 font-mono">({total} total)</span>
        </div>
      </div>

      {total === 0 ? (
        <div className="text-sm text-neutral-500 py-4 text-center">
          No {SEGMENT_LABELS[segmentKey].toLowerCase()} this week yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((src) => {
            const cur = thisWeekSources[src] || 0;
            const prev = lastWeekSources[src] || 0;
            const delta = wow(cur, prev);
            const pct = Math.round((cur / maxCount) * 100);
            return (
              <div
                key={src}
                data-testid={`source-row-${src}`}
                className="flex items-center gap-4"
              >
                <div className="w-32 shrink-0 text-sm text-neutral-300">
                  {SOURCE_LABELS[src]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className={`h-full ${SOURCE_COLORS[src]} rounded-full transition-all duration-300`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="w-16 shrink-0 text-right font-mono text-sm font-semibold">
                  {cur}
                </div>
                <div
                  className={`w-24 shrink-0 text-right text-[11px] font-mono ${
                    delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-400" : "text-neutral-500"
                  }`}
                >
                  {cur === 0 && prev === 0 ? "—" : delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${delta}% wk/wk`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function leadOrigin(l) {
  const parts = [];
  const ch = (l.utm_source || "").toLowerCase();
  if (ch === "reels_page" || ch === "reels") {
    parts.push(`Reel${l.utm_content ? `: ${l.utm_content}` : "s page"}`);
  } else if (ch) {
    parts.push(ch.charAt(0).toUpperCase() + ch.slice(1));
  }
  if (l.landing_path) parts.push(`landed on ${l.landing_path.split("?")[0]}`);
  if (!parts.length && l.referrer) {
    try {
      parts.push(`via ${new URL(l.referrer).hostname}`);
    } catch {
      parts.push("via external link");
    }
  }
  return parts.length ? parts.join(" · ") : "Direct / unknown";
}

function LeadSourcesStrip() {
  const [report, setReport] = useState(null);
  useEffect(() => {
    client.get("/admin/lead-sources").then((r) => setReport(r.data)).catch(() => {});
  }, []);
  if (!report || !report.total_leads) return null;
  const chip = (label, count, key, accent) => (
    <span
      key={key}
      data-testid={`lead-source-chip-${key}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-mono border ${
        accent
          ? "border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF9764]"
          : "border-white/10 bg-white/[0.04] text-neutral-300"
      }`}
    >
      {label} <b className="text-white">{count}</b>
    </span>
  );
  return (
    <div className="mt-6 card-surface p-4" data-testid="inbox-lead-sources">
      <div className="flex items-center gap-2 mb-2.5">
        <TrendUp size={14} weight="bold" color="#FF5A1F" />
        <span className="overline">WHAT'S DRIVING LEADS · LAST 30 DAYS · {report.total_leads} TOTAL</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {(report.by_channel || []).map((c, i) => chip(c.key, c.count, `ch-${c.key}`, i === 0))}
        {(report.by_reel || []).slice(0, 2).map((r) => chip(`reel ${r.key}`, r.count, `reel-${r.key}`, true))}
        {(report.by_landing || []).slice(0, 3).map((p) => chip(p.key, p.count, `page-${p.key.replace(/\//g, "_")}`))}
      </div>
    </div>
  );
}

export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [wonPromptOpen, setWonPromptOpen] = useState(false);
  const [dealValue, setDealValue] = useState("");
  // Multi-select tier filter — empty set = "All"
  const [activeTiers, setActiveTiers] = useState(new Set());
  const [heatSort, setHeatSort] = useState(false);
  const [coldCount, setColdCount] = useState(0);
  const [coldBusy, setColdBusy] = useState(false);

  useEffect(() => {
    client.get("/admin/cold-revive/preview").then((r) => setColdCount(r.data.count)).catch(() => {});
  }, []);

  const coldBlast = async () => {
    setColdBusy(true);
    try {
      const { data } = await client.post("/admin/cold-revive/blast");
      toast.success(
        data.sent_count > 0
          ? `Fresh angle sent to ${data.sent_count} cold lead${data.sent_count === 1 ? "" : "s"}`
          : "No cold leads needed reviving"
      );
      setColdCount(0);
    } catch {
      toast.error("Cold revive failed");
    } finally {
      setColdBusy(false);
    }
  };
  // Which weekly-stat card is expanded to show source-attribution breakdown
  const [expandedSegment, setExpandedSegment] = useState(null);
  const [testLeadCount, setTestLeadCount] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/contact-requests");
      setItems(data);
      client.get("/admin/leads-test-data/preview").then((r) => setTestLeadCount(r.data.count || 0)).catch(() => {});
    } catch (e) {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    if (status === "won") {
      setDealValue(selected?.deal_value_monthly != null ? String(selected.deal_value_monthly) : "");
      setWonPromptOpen(true);
      return;
    }
    try {
      await client.patch(`/admin/contact-requests/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const confirmWon = async () => {
    const trimmed = dealValue.trim();
    if (trimmed !== "" && (Number.isNaN(parseFloat(trimmed)) || parseFloat(trimmed) < 0)) {
      return toast.error("Enter a valid dollar amount");
    }
    try {
      const payload = trimmed !== "" ? { deal_value_monthly: parseFloat(trimmed) } : {};
      const { data } = await client.post(`/admin/contact-requests/${selected.id}/mark-won`, payload);
      const fields = {
        status: "won",
        won_at: data.won_at,
        ...(data.deal_value_monthly != null ? { deal_value_monthly: data.deal_value_monthly } : {}),
        ...(data.won_thankyou_sent_at ? { won_thankyou_sent_at: data.won_thankyou_sent_at } : {}),
      };
      setItems((prev) => prev.map((i) => (i.id === selected.id ? { ...i, ...fields } : i)));
      setSelected((prev) => ({ ...prev, ...fields }));
      setWonPromptOpen(false);
      playCashSound();
      toast.success(data.won_thankyou_sent_at ? "Marked as won — thank-you email sent" : "Marked as won");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await client.delete(`/admin/contact-requests/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const isDue = (l) => l.next_followup_at && new Date(l.next_followup_at) <= new Date();

  const setReminder = async (remindAt) => {
    try {
      await client.post(`/admin/contact-requests/${selected.id}/reminder`, { remind_at: remindAt });
      setItems((prev) => prev.map((i) => (i.id === selected.id ? { ...i, next_followup_at: remindAt || null } : i)));
      setSelected((prev) => ({ ...prev, next_followup_at: remindAt || null }));
      toast.success(remindAt ? `Reminder set for ${remindAt}` : "Reminder cleared");
    } catch { toast.error("Update failed"); }
  };

  const cleanupTestData = async () => {
    if (!window.confirm(`Delete ${testLeadCount} test lead${testLeadCount === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      const { data } = await client.post("/admin/leads-test-data/cleanup");
      toast.success(`Removed ${data.deleted} test lead${data.deleted === 1 ? "" : "s"}`);
      setSelected(null);
      setTestLeadCount(0);
      load();
    } catch { toast.error("Cleanup failed"); }
  };

  // Compute per-tier counts across ALL items (not filtered) so counts stay stable
  const tierCounts = items.reduce(
    (acc, it) => {
      const t = fleetTier(it.fleet_size);
      const key = t ? t.label : "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    },
    {},
  );

  // Filter items by active tiers (empty set = show all)
  const filteredItems = activeTiers.size === 0
    ? items
    : items.filter((it) => {
        const t = fleetTier(it.fleet_size);
        const key = t ? t.label : "Unknown";
        return activeTiers.has(key);
      });

  const displayedItems = heatSort
    ? [...filteredItems].sort((a, b) => heatScore(b) - heatScore(a))
    : filteredItems;

  const toggleTier = (label) => {
    setActiveTiers((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const newCount = items.filter((i) => i.status === "new").length;
  const dueCount = items.filter(isDue).length;

  // Weekly demand-signal stats — this-week vs last-week counts per segment.
  // Computed client-side from `created_at` so no new endpoint required.
  // Windows are trailing 7-day, aligned to "now" (rolling, not calendar-week).
  const now = Date.now();
  const week = 7 * 24 * 60 * 60 * 1000;
  const inRange = (iso, start, end) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return t >= start && t < end;
  };
  const bucket = (arr) => {
    // Track total tier counts AND per-tier source attribution simultaneously.
    // Empty source map for every tier so drill-down always shows all 4 rows in
    // a consistent order (zero-value rows are still valuable signal).
    const emptySources = () => ({
      free_assessment: 0,
      demo_booking: 0,
      roi_calculator: 0,
      contact_page: 0,
    });
    const b = {
      total: 0,
      highArr: 0,
      smb: 0,
      ownerOp: 0,
      sources: {
        total: emptySources(),
        highArr: emptySources(),
        smb: emptySources(),
        ownerOp: emptySources(),
      },
    };
    for (const it of arr) {
      const t = fleetTier(it.fleet_size);
      const src = it.source || "contact_page";
      b.total += 1;
      if (b.sources.total[src] != null) b.sources.total[src] += 1;
      const label = t?.label;
      if (label === "Growth" || label === "Mid-Market" || label === "Enterprise") {
        b.highArr += 1;
        if (b.sources.highArr[src] != null) b.sources.highArr[src] += 1;
      } else if (label === "SMB") {
        b.smb += 1;
        if (b.sources.smb[src] != null) b.sources.smb[src] += 1;
      } else if (label === "Owner-Op") {
        b.ownerOp += 1;
        if (b.sources.ownerOp[src] != null) b.sources.ownerOp[src] += 1;
      }
    }
    return b;
  };
  const thisWeek = bucket(items.filter((i) => inRange(i.created_at, now - week, now)));
  const lastWeek = bucket(items.filter((i) => inRange(i.created_at, now - 2 * week, now - week)));
  const pctChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return Math.round(((curr - prev) / prev) * 100);
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="inbox-page">
      <PageHeader
        overline="ADMIN"
        title="Inbox"
        subtitle={`${items.length} request${items.length === 1 ? "" : "s"} · ${newCount} new${dueCount > 0 ? ` · ${dueCount} follow-up${dueCount === 1 ? "" : "s"} due` : ""}`}
        action={
          <div className="flex items-center gap-2">
            <TwilioStatusBadge />
            {testLeadCount > 0 && (
              <button
                data-testid="cleanup-test-data-btn"
                onClick={cleanupTestData}
                className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-red-500/30 text-red-300 hover:border-red-400 transition-colors"
              >
                <Trash size={14} weight="bold" /> Clean test data ({testLeadCount})
              </button>
            )}
            <button
              data-testid="inbox-refresh-btn"
              onClick={load}
              className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 hover:border-white/20 text-neutral-300 hover:text-white transition-colors"
            >
              <ArrowClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>
        }
      />

      {/* Weekly demand-signal stat bar — 4 stat pills, WoW deltas.
          Renders after items load (skipped while loading to avoid flashing zeros).
          Clicking any card expands a source-attribution drill-down below. */}
      {!loading && items.length > 0 && (
        <>
          <div
            data-testid="inbox-weekly-stats"
            className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <WeekStatCard
              label="This week"
              value={thisWeek.total}
              delta={pctChange(thisWeek.total, lastWeek.total)}
              variant="neutral"
              testid="weekstat-total"
              expanded={expandedSegment === "total"}
              onClick={() => setExpandedSegment(expandedSegment === "total" ? null : "total")}
            />
            <WeekStatCard
              label="High-ARR"
              value={thisWeek.highArr}
              delta={pctChange(thisWeek.highArr, lastWeek.highArr)}
              variant="orange"
              hot={pctChange(thisWeek.highArr, lastWeek.highArr) >= 30 && thisWeek.highArr > 0}
              testid="weekstat-high-arr"
              expanded={expandedSegment === "highArr"}
              onClick={() => setExpandedSegment(expandedSegment === "highArr" ? null : "highArr")}
            />
            <WeekStatCard
              label="SMB"
              value={thisWeek.smb}
              delta={pctChange(thisWeek.smb, lastWeek.smb)}
              variant="emerald"
              hot={pctChange(thisWeek.smb, lastWeek.smb) >= 30 && thisWeek.smb > 0}
              testid="weekstat-smb"
              expanded={expandedSegment === "smb"}
              onClick={() => setExpandedSegment(expandedSegment === "smb" ? null : "smb")}
            />
            <WeekStatCard
              label="Owner-Op"
              value={thisWeek.ownerOp}
              delta={pctChange(thisWeek.ownerOp, lastWeek.ownerOp)}
              variant="sky"
              hot={pctChange(thisWeek.ownerOp, lastWeek.ownerOp) >= 30 && thisWeek.ownerOp > 0}
              testid="weekstat-owner-op"
              expanded={expandedSegment === "ownerOp"}
              onClick={() => setExpandedSegment(expandedSegment === "ownerOp" ? null : "ownerOp")}
            />
          </div>

          {expandedSegment && (
            <SourceBreakdownPanel
              segmentKey={expandedSegment}
              thisWeekSources={thisWeek.sources[expandedSegment]}
              lastWeekSources={lastWeek.sources[expandedSegment]}
              onClose={() => setExpandedSegment(null)}
            />
          )}
        </>
      )}

      <LeadSourcesStrip />

      <FollowupPayoffStrip />

      <SignupWinPathCard />

      <ReferralThanksCard />

      <NudgeEffectivenessCard />
      <PlaybookNudgeCard />
      <SignupSourcesCard />
      <SourceRoiCard />
      <ChatConversationsCard />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 card-surface overflow-hidden" data-testid="inbox-list">
          {/* Tier filter chip row — multi-select, empty selection = show all */}
          <div className="p-3 border-b border-white/10 bg-black/30 sticky top-0 z-10 backdrop-blur-sm">
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="inbox-tier-filter">
              <FilterChip
                label="All"
                count={items.length}
                active={activeTiers.size === 0}
                onClick={() => setActiveTiers(new Set())}
                testid="filter-all"
                variant="neutral"
              />
              {(() => {
                const preset = new Set(["Growth", "Mid-Market", "Enterprise"]);
                const isActive =
                  activeTiers.size === preset.size &&
                  [...preset].every((t) => activeTiers.has(t));
                const highArrCount =
                  (tierCounts["Growth"] || 0) +
                  (tierCounts["Mid-Market"] || 0) +
                  (tierCounts["Enterprise"] || 0);
                return (
                  <FilterChip
                    label="High-ARR"
                    count={highArrCount}
                    active={isActive}
                    onClick={() => setActiveTiers(isActive ? new Set() : preset)}
                    testid="filter-preset-high-arr"
                    variant="orange"
                  />
                );
              })()}
              <span className="w-px h-4 bg-white/10 mx-0.5" aria-hidden />
              <button
                data-testid="inbox-heat-sort"
                onClick={() => setHeatSort((v) => !v)}
                title="Rank leads hot-to-cold by fleet size, source, engagement and freshness"
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  heatSort
                    ? "border-[#FF5A1F]/60 bg-[#FF5A1F]/15 text-[#FF9764]"
                    : "border-white/10 text-neutral-400 hover:border-white/25"
                }`}
              >
                <Flame size={11} weight={heatSort ? "fill" : "regular"} /> Hottest first
              </button>
              {coldCount > 0 && (
                <button
                  data-testid="cold-revive-blast"
                  onClick={coldBlast}
                  disabled={coldBusy}
                  title="One-tap: email a fresh angle to every cold lead (7+ days old, once per lead)"
                  className={`inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[10px] font-semibold text-sky-300 hover:bg-sky-500/20 transition-colors ${coldBusy ? "opacity-50" : ""}`}
                >
                  <Snowflake size={11} weight="bold" />
                  {coldBusy ? "Reviving…" : `Revive ${coldCount} cold`}
                </button>
              )}
              <span className="w-px h-4 bg-white/10 mx-0.5" aria-hidden />
              {[
                { key: "Owner-Op", variant: "sky" },
                { key: "SMB", variant: "emerald" },
                { key: "Growth", variant: "orange" },
                { key: "Mid-Market", variant: "violet" },
                { key: "Enterprise", variant: "rose" },
                { key: "Unknown", variant: "neutral" },
              ].filter((t) => (tierCounts[t.key] || 0) > 0 || t.key !== "Unknown")
                .map((t) => (
                  <FilterChip
                    key={t.key}
                    label={t.key}
                    count={tierCounts[t.key] || 0}
                    active={activeTiers.has(t.key)}
                    onClick={() => toggleTier(t.key)}
                    testid={`filter-${t.key.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    variant={t.variant}
                  />
                ))}
            </div>
            {activeTiers.size > 0 && (
              <div className="text-[11px] text-neutral-500 mt-2 flex items-center justify-between">
                <span>
                  Showing <b className="text-neutral-300">{filteredItems.length}</b> of{" "}
                  <b className="text-neutral-300">{items.length}</b>
                </span>
                <button
                  onClick={() => setActiveTiers(new Set())}
                  data-testid="filter-clear"
                  className="text-[#FF5A1F] hover:text-[#FF7038] font-medium"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto scrollbar-thin">
            {loading ? (
              <div className="p-8 text-center text-neutral-500 text-sm">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="p-12 text-center" data-testid="inbox-empty">
                <div className="overline mb-2">
                  {items.length === 0 ? "EMPTY" : "NO MATCHES"}
                </div>
                <p className="text-neutral-500 text-sm">
                  {items.length === 0
                    ? "No contact requests yet."
                    : "No leads match the current tier filter."}
                </p>
              </div>
            ) : displayedItems.map((it) => (
              <button
                key={it.id}
                data-testid={`inbox-item-${it.id}`}
                onClick={() => { setSelected(it); setWonPromptOpen(false); }}
                className={`w-full text-left p-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors ${
                  selected?.id === it.id ? "bg-white/[0.03]" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="font-medium text-sm truncate flex-1 min-w-0">{it.company_name}</div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {replyWaitHours(it) != null && (
                      <span
                        data-testid={`inbox-reply-timer-${it.id}`}
                        title="Waiting for your first reply — hot leads go cold fast"
                        className="inline-flex items-center gap-0.5 rounded-full border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 text-[9px] font-bold text-red-300"
                      >
                        <Timer size={10} weight="bold" /> {waitLabel(replyWaitHours(it))}
                      </span>
                    )}
                    {isDue(it) && (
                      <BellRinging
                        size={13}
                        weight="fill"
                        className="text-amber-400"
                        data-testid={`inbox-due-flag-${it.id}`}
                      />
                    )}
                    <FleetTag fleetSize={it.fleet_size} size="sm" />
                    {it.status !== "won" && it.email_trusted !== false && (() => {
                      const hs = heatScore(it);
                      const hb = heatBadge(hs);
                      return (
                        <span
                          data-testid={`inbox-heat-${it.id}`}
                          title={`Heat score ${hs}/100 — fleet size, source, engagement, freshness`}
                          className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${hb.cls}`}
                        >
                          <Flame size={9} weight={hb.label === "HOT" ? "fill" : "regular"} /> {hb.label} {hs}
                        </span>
                      );
                    })()}
                    <StatusDot status={it.status} />
                  </div>
                </div>
                <div className="text-xs text-neutral-400 truncate flex items-center gap-1">
                  {it.email_trusted === false && (
                    <Warning
                      size={12}
                      weight="fill"
                      className="text-amber-400 shrink-0"
                      data-testid={`inbox-junk-flag-${it.id}`}
                    />
                  )}
                  <span className="truncate">{it.contact_name} · {it.email}</span>
                </div>
                <div className="text-[10px] text-neutral-600 font-mono mt-1">
                  {new Date(it.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <div className="card-surface p-8" data-testid="inbox-detail">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="overline">{selected.source || "CONTACT_PAGE"}</div>
                    <FleetTag fleetSize={selected.fleet_size} size="lg" />
                  </div>
                  <h2 className="font-display text-3xl font-semibold tracking-tight">{selected.company_name}</h2>
                  <p className="text-neutral-400 mt-1">{selected.contact_name}</p>
                </div>
                <button
                  data-testid="inbox-delete-btn"
                  onClick={() => remove(selected.id)}
                  className="w-9 h-9 rounded-md hover:bg-red-500/15 text-neutral-500 hover:text-red-400 flex items-center justify-center transition-colors"
                >
                  <Trash size={16} />
                </button>
              </div>

              {selected.next_followup_at && (
                <div
                  data-testid="followup-reminder-banner"
                  className={`mb-6 flex flex-wrap items-center gap-3 rounded-md border p-3.5 text-sm ${
                    isDue(selected)
                      ? "border-amber-500/40 bg-amber-500/15 text-amber-200"
                      : "border-white/10 bg-white/[0.03] text-neutral-300"
                  }`}
                >
                  <BellRinging
                    size={16}
                    weight={isDue(selected) ? "fill" : "bold"}
                    className={isDue(selected) ? "text-amber-400" : "text-neutral-400"}
                  />
                  <span className="flex-1 min-w-[180px]">
                    {isDue(selected) ? "Follow-up due — time to call back" : "Follow-up scheduled"}
                    {" · "}
                    <b data-testid="reminder-date">{String(selected.next_followup_at).slice(0, 10)}</b>
                  </span>
                  <button
                    data-testid="reminder-done-btn"
                    onClick={() => setReminder(null)}
                    className="rounded-full px-3.5 py-1.5 text-xs bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition-colors"
                  >
                    Done
                  </button>
                  <button
                    data-testid="reminder-snooze-btn"
                    onClick={() => setReminder(new Date(Date.now() + 2 * 864e5).toISOString().slice(0, 10))}
                    className="rounded-full px-3 py-1.5 text-xs border border-white/15 text-neutral-300 hover:border-white/30 transition-colors"
                  >
                    +2 days
                  </button>
                </div>
              )}

              {selected.email_trusted === false && (
                <div
                  data-testid="inbox-junk-email-banner"
                  className="mb-6 flex items-start gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3.5 text-sm text-amber-200"
                >
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-amber-400" />
                  <div>
                    <b>Likely fake email</b> — {selected.email_flag_reason || "failed trust checks"}.
                    Treat the phone number as the only contact channel. No confirmation email was sent to this address.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <ContactField icon={<EnvelopeSimple size={14} />} label="EMAIL" value={selected.email} href={`mailto:${selected.email}`} />
                <ContactField icon={<Phone size={14} />} label="PHONE" value={selected.phone} href={`tel:${selected.phone}`} />
                <ContactField icon={<Buildings size={14} />} label="FLEET SIZE" value={selected.fleet_size || "—"} />
                <ContactField icon={<Buildings size={14} />} label="SERVICE INTEREST" value={selected.services_of_interest || "—"} />
                <ContactField icon={<TrendUp size={14} />} label="CAME FROM" value={leadOrigin(selected)} testid="inbox-lead-origin" />
              </div>

              {selected.current_challenges && (
                <div className="mb-6">
                  <div className="overline mb-2">CHALLENGES</div>
                  <div className="p-4 rounded-md bg-[#0F0F0F] border border-white/5 text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed">
                    {selected.current_challenges}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <div className="overline mb-2">STATUS</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      data-testid={`inbox-status-${s.value}`}
                      onClick={() => updateStatus(selected.id, s.value)}
                      className={`pill pill-${s.pill} ${selected.status === s.value ? "ring-2 ring-offset-2 ring-offset-[#141414] ring-white/20" : "opacity-60 hover:opacity-100"} transition-all cursor-pointer`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                {wonPromptOpen && (
                  <div
                    data-testid="won-deal-panel"
                    className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3"
                  >
                    <span className="text-xs text-emerald-200">Deal value</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-emerald-300">$</span>
                      <input
                        data-testid="won-deal-value-input"
                        type="number"
                        min="0"
                        value={dealValue}
                        onChange={(e) => setDealValue(e.target.value)}
                        placeholder="0"
                        className="w-24 bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:border-emerald-400/60"
                      />
                      <span className="text-xs text-neutral-400">/month (optional)</span>
                    </div>
                    <button
                      data-testid="won-deal-confirm-btn"
                      onClick={confirmWon}
                      className="rounded-full px-3.5 py-1.5 text-xs bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition-colors"
                    >
                      Mark Won
                    </button>
                    <button
                      data-testid="won-deal-cancel-btn"
                      onClick={() => setWonPromptOpen(false)}
                      className="rounded-full px-3 py-1.5 text-xs border border-white/15 text-neutral-300 hover:border-white/30 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {selected.status === "won" && selected.deal_value_monthly != null && (
                  <div data-testid="won-deal-chip" className="mt-2 text-[11px] text-emerald-400 font-mono">
                    Won{selected.won_at ? ` ${new Date(selected.won_at).toLocaleDateString()}` : ""} · ${selected.deal_value_monthly.toLocaleString()}/mo
                    {selected.won_thankyou_sent_at && <span className="text-emerald-500/70"> · thank-you sent</span>}
                  </div>
                )}
              </div>

              {(selected.signed_up_at || selected.first_unpaid_load_at) && (
                <div className="mb-6 flex flex-wrap gap-2" data-testid="lead-progress-badges">
                  {selected.signed_up_at && (
                    <span
                      data-testid="lead-progress-signed-up"
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-300 font-mono"
                    >
                      <CheckCircle size={12} weight="fill" /> Signed up {new Date(selected.signed_up_at).toLocaleDateString()}
                    </span>
                  )}
                  {selected.first_unpaid_load_at && (
                    <span
                      data-testid="lead-progress-first-load"
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-300 font-mono"
                    >
                      <CheckCircle size={12} weight="fill" /> First unpaid load {new Date(selected.first_unpaid_load_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

              <LeadReplyTools
                lead={selected}
                onUpdate={(fields) => {
                  setSelected((prev) => ({ ...prev, ...fields }));
                  setItems((prev) => prev.map((i) => (i.id === selected.id ? { ...i, ...fields } : i)));
                }}
              />

              <LeadCallLog
                lead={selected}
                onUpdate={(fields) => {
                  setSelected((prev) => ({ ...prev, ...fields }));
                  setItems((prev) => prev.map((i) => (i.id === selected.id ? { ...i, ...fields } : i)));
                }}
              />

              {selected.followup_sent_at && (
                <div data-testid="inbox-followup-stamp" className="mb-1 text-[11px] text-neutral-500 font-mono">
                  48h auto-nudge sent {new Date(selected.followup_sent_at).toLocaleString()}
                </div>
              )}
              {selected.followup2_sent_at && (
                <div data-testid="inbox-followup2-stamp" className="mb-4 text-[11px] text-neutral-500 font-mono">
                  Day-5 last note sent {new Date(selected.followup2_sent_at).toLocaleString()}
                </div>
              )}

              <div className="pt-6 border-t border-white/5 flex flex-wrap gap-3">
                {selected.email_trusted === false ? (
                  <>
                    <a
                      href={`tel:${selected.phone}`}
                      data-testid="inbox-call-btn"
                      className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"
                    >
                      <Phone size={14} weight="bold" /> Call — only reliable contact
                    </a>
                    <span
                      data-testid="inbox-reply-email"
                      aria-disabled="true"
                      title="Email flagged as fake — replies would bounce"
                      className="rounded-full px-5 py-2.5 text-sm border border-white/10 text-neutral-500 inline-flex items-center gap-2 cursor-not-allowed select-none"
                    >
                      <EnvelopeSimple size={14} weight="bold" /> Email unverified
                    </span>
                  </>
                ) : (
                  <>
                    <a
                      href={`mailto:${selected.email}?subject=Re: Your FreightPilot AI consultation request&body=Hi ${selected.contact_name.split(" ")[0]},%0D%0A%0D%0AThanks for reaching out to FreightPilot AI. I'd love to schedule a quick 15-minute call to walk through your workflow and identify where AI can save you time.%0D%0A%0D%0AWhen works best for you this week?%0D%0A%0D%0AMike Gee%0D%0AFounder · FreightPilot AI LLC%0D%0A(605) 679-7303%0D%0Acontact@freightpilotai.ai`}
                      data-testid="inbox-reply-email"
                      className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2"
                    >
                      <EnvelopeSimple size={14} weight="bold" /> Reply by email
                    </a>
                    <a
                      href={`tel:${selected.phone}`}
                      data-testid="inbox-call-btn"
                      className="rounded-full px-5 py-2.5 text-sm border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors inline-flex items-center gap-2"
                    >
                      <Phone size={14} weight="bold" /> Call
                    </a>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="card-surface p-16 text-center">
              <div className="overline mb-2">SELECT A REQUEST</div>
              <p className="text-neutral-500 text-sm">Click a contact request on the left to view details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const c = {
    new: "bg-[#60a5fa]", replied: "bg-[#a78bfa]", contacted: "bg-[#fbbf24]", won: "bg-[#34d399]", lost: "bg-[#f87171]",
  }[status] || "bg-neutral-500";
  return <span className={`dot ${c} mt-1.5`} />;
}

function ContactField({ icon, label, value, href, testid }) {
  const content = <div className="text-sm text-neutral-100" data-testid={testid}>{value}</div>;
  return (
    <div className="p-4 rounded-md bg-[#0F0F0F] border border-white/5">
      <div className="flex items-center gap-2 text-neutral-500 mb-1">{icon}<span className="overline">{label}</span></div>
      {href ? <a href={href} className="hover:text-[#FF5A1F] transition-colors">{content}</a> : content}
    </div>
  );
}
